/** Feedback display — shows post-answer result header and optional explanation/feedback. */
import { motion } from 'motion/react';
import { Check, X, CircleAlert } from 'lucide-react';
import type { StepResult } from '../types';
import { getGradingOutcome } from '../sessionUtils';

interface FeedbackDisplayProps {
  result: StepResult;
}

const OUTCOME_CONFIG = {
  correct: {
    bg: 'var(--f-success-subtle)',
    text: 'var(--f-success-text)',
    iconBg: 'var(--f-success)',
    Icon: Check,
    label: 'Correct',
  },
  partial: {
    bg: 'var(--f-warning-subtle)',
    text: 'var(--f-warning-text)',
    iconBg: 'var(--f-warning)',
    Icon: CircleAlert,
    label: 'Partially Correct',
  },
  incorrect: {
    bg: 'var(--f-error-subtle)',
    text: 'var(--f-error-text)',
    iconBg: 'var(--f-error)',
    Icon: X,
    label: 'Incorrect',
  },
} as const;

/**
 * Renders post-answer feedback with correct/partial/incorrect styling.
 * Always renders the header (icon + label). Explanation and feedback text
 * are shown below when available.
 */
export function FeedbackDisplay({ result }: FeedbackDisplayProps) {
  const outcome = getGradingOutcome(result);
  const { bg, text, iconBg, Icon, label } = OUTCOME_CONFIG[outcome];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.15, ease: 'easeOut' }}
      className="rounded-xl p-5"
      style={{
        background: bg,
        marginTop: 'var(--f-space-4)',
        fontFamily: 'var(--f-font-sans)',
      }}
    >
      <div className="flex items-center gap-2">
        <div
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
          style={{ background: iconBg }}
        >
          <Icon size={14} color="white" strokeWidth={3} />
        </div>
        <span className="font-semibold" style={{ color: text, fontSize: 'var(--f-text-lg)' }}>
          {label}
        </span>
      </div>

      {(result.explanation || result.feedback) && (
        <div
          className="mt-3 flex flex-col gap-2"
          style={{
            color: text,
            fontSize: 'var(--f-text-sm)',
            lineHeight: 'var(--f-leading-relaxed)',
          }}
        >
          {result.explanation && <p>{result.explanation}</p>}
          {result.feedback && <p>{result.feedback}</p>}
        </div>
      )}
    </motion.div>
  );
}
